// src/components/timetable/modern/modernTimetableTypes.ts
//
// Ported from the mobile app's app/StackScreens/Timetable/timetableTypes.ts.
// Column-width constants that depended on RN's Dimensions API are dropped —
// the web grid uses CSS (min-width / flex) instead of fixed pixel columns.

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TimetableItem {
  id: string;
  class_id?: string;
  class_name?: string;
  section: string | null;
  subject_id: string | null;
  subject_name: string;
  teacher_id: string | null;
  teacher_name: string;
  period_label: string;
  period_number?: number;
  start_time: string;
  end_time: string;
  slot_type: string;
  // Go API returns this as a real JSON array (e.g. ["Monday","Tuesday"]) —
  // see parseDaysOfWeek below for the defensive fallback.
  day_of_week: DayOfWeek[];
  room_number: string | null;
  created_at: string;
}

export interface ClassTimetable {
  class_id: string;
  class_name: string;
  schedule: TimetableItem[];
}

export interface ApiResponse {
  message: string;
  status: string;
  data: {
    timetable_type_id: string;
    timetable_type_name: string;
    session_id?: string;
    total_items: number;
    total_classes: number;
    classes: ClassTimetable[];
  };
}

export interface AttendanceRecord {
  id: number;
  user_id: number;
  user_role: string;
  attendance_date: string;
  scan_type: string;
  scan_time: string;
  status: string;
  user_name: string;
  user_email: string;
}

export interface AttendanceApiResponse {
  status: string;
  message: string;
  data: {
    records: AttendanceRecord[];
    pagination: {
      current_page: number;
      per_page: number;
      total_items: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    };
  };
}

export interface ClassSection {
  classId: string;
  className: string;
  section: string | null;
}

// A timetable type as returned by GET /timetable-types (ObjectID-backed).
// `value` holds the Mongo ObjectID hex string.
export interface TimetableTypeOption {
  label: string;
  value: string;
  status?: string; // 'active' | 'inactive'
}

export type DayOfWeek = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';
export type ViewMode  = 'classes' | 'all_teachers' | 'single_teacher' | 'vacant_teachers' | 'arrangements';

// Per-period data for the Arrangements view
export interface ArrangementPeriod {
  period:         string;
  startTime:      string;
  endTime:        string;
  isBreak:        boolean;
  isLive:         boolean;
  // Teachers free this period (scheduled but vacant = no class assigned)
  vacantTeachers: string[];
  // Teachers scheduled but not checked in today
  absentTeachers: { name: string; affectedSlots: AffectedSlot[] }[];
}

export interface AffectedSlot {
  teacherName: string;
  className:   string;
  section:     string | null;
  subject:     string;
}

// Teacher arrangement from API
export interface TeacherArrangement {
  id:                    string;
  class_id:              string;
  class_name:            string;
  section:               string | null;
  subject_id:            string;
  subject_name:          string;
  arranged_teacher_id:   string;
  arranged_teacher_name: string;
  primary_teacher_id:    string;
  primary_teacher_name:  string;
  arranged_at:           string;
}

export interface TeacherArrangementsApiResponse {
  status:  string;
  message?: string;
  total:   number;
  data:    TeacherArrangement[];
}

export interface AddArrangementPayload {
  class_id:             string;
  subject_id:           string;
  arranged_teacher_id:  string;
  primary_teacher_id:   string;
  section?:             string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

// Fixed column widths for the grid (px). Kept close to the mobile app's
// PERIOD_COL_W / CLASS_COL_W / ROW_H so the two views feel like the same
// product — on web these become CSS pixel values instead of RN Dimensions math.
export const PERIOD_COL_W = 110;
export const CLASS_COL_W  = 200;
export const ST_PERIOD_W  = 140;
export const ROW_H        = 78;
export const HEADER_H     = 52;

export const DAYS_OF_WEEK: DayOfWeek[] = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];

// ─── Colors ───────────────────────────────────────────────────────────────────
// Same palette as the mobile app (C.primary matches the web admin's existing
// navy gradient in TimeSlotsManager: #1e3a8a → #2855ae), so Modern Timetable
// sits visually consistent with both surfaces.

export const C = {
  primary:       '#2855AE',
  primaryDark:   '#1e3a8a',
  primaryMid:    '#3A6BC7',
  primaryBg:     'rgba(40,85,174,0.08)',
  primaryBgDk:   'rgba(40,85,174,0.14)',
  background:    '#F5F7FA',
  cardBg:        '#FFFFFF',
  border:        '#E5E7EB',
  borderDk:      '#D1D5DB',
  textPrimary:   '#1a1a1ae1',
  textSecondary: '#5a5f69ff',
  textLight:     '#6B7280',
  break:         '#F59E0B',
  breakBg:       'rgba(245,158,11,0.08)',
  vacant:        '#10B981',
  vacantBg:      'rgba(16,185,129,0.09)',
  live:          '#16a34a',
  present:       '#B45309',
  presentBg:     'rgba(251,191,36,0.13)',
  presentBorder: 'rgba(251,191,36,0.55)',
  presentHeader: 'rgba(254,243,199,0.85)',
  absent:        '#DC2626',
  absentBg:      'rgba(220,38,38,0.07)',
  absentBorder:  'rgba(220,38,38,0.3)',
  affected:      '#0D9488',
  affectedBg:    'rgba(13,148,136,0.07)',
  affectedBorder:'rgba(13,148,136,0.3)',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const toMinutes = (t: string): number => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

export const nowMinutes = (): number => {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
};

export const isCurrentPeriod = (startTime: string, endTime: string): boolean => {
  const now = nowMinutes();
  return now >= toMinutes(startTime) && now < toMinutes(endTime);
};

export const formatTime = (time: string): string => {
  const [h, m] = time.split(':').map(Number);
  const p = h >= 12 ? 'pm' : 'am';
  return `${h % 12 || 12}:${m.toString().padStart(2, '0')}${p}`;
};

// The Go API sends day_of_week as a real JSON array. Kept defensive against a
// JSON-encoded-string shape too, in case any stale data/endpoint still
// returns it that way (mirrors the mobile app's parseDaysOfWeek).
export const parseDaysOfWeek = (raw: DayOfWeek[] | string | null | undefined): DayOfWeek[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return []; }
};

export const isScheduledForDay = (item: TimetableItem, day: DayOfWeek): boolean =>
  parseDaysOfWeek(item.day_of_week).includes(day);

export const todayDate = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const initials = (name: string): string =>
  name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
